import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, Clock, Users, ChevronDown, Trash2, Youtube, Tv } from 'lucide-react';
import { ServerListItem, SavedServer, Championship } from '../types';
import './ServerList.css';

const getShortTimezoneCode = (timezone: string): string => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(now);
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value || timezone.split('/')[1] || 'UTC';
};

  const formatScheduledStartValue = (dateTime: string, timezone: string): string => {
    if (!dateTime) {
      return '';
    }

    const absoluteTime = /[Zz]$|[+-][0-9]{2}:[0-9]{2}$/.test(dateTime);
    if (absoluteTime) {
      const date = new Date(dateTime);
      if (Number.isNaN(date.getTime())) {
        return dateTime;
      }

      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      return formatter.format(date);
    }

    const [datePart, timePart] = dateTime.split('T');
    if (!datePart || !timePart) {
      return dateTime;
    }

    return `${datePart} ${timePart.split('.')[0].slice(0, 5)}`;
  };
interface ServerListProps {
  servers: SavedServer[];
  selectedId: string | null;
  championships?: Championship[];
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  onSelect: (server: ServerListItem) => void;
  onLaunch: (serverId: string) => void;
  onStop: (serverId: string) => void;
  onDelete: (serverId: string) => void;
}

const buildBroadcastUrl = (platform: string, channel: string): string => {
  if (!channel) return '';
  if (platform === 'youtube') {
    if (channel.startsWith('http')) return channel;
    const handle = channel.startsWith('@') ? channel : `@${channel}`;
    return `https://youtube.com/${handle}/live`;
  }
  if (platform === 'twitch') {
    if (channel.startsWith('http')) return channel;
    return `https://twitch.tv/${channel}`;
  }
  return '';
};

const ServerList: React.FC<ServerListProps> = ({
  servers,
  selectedId,
  championships = [],
  broadcastPlatform = '',
  broadcastChannel = '',
  onSelect,
  onLaunch,
  onStop,
  onDelete,
}) => {
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'port' | 'players'>('name');
  const [sortOpen, setSortOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };

    if (sortOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [sortOpen]);

  // Filter and sort servers
  const filteredServers = servers
    .filter((server) => 
      server.name.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
      if (sortBy === 'port') return (a.serverConfig.server_tcp_listener_port || 0) - (b.serverConfig.server_tcp_listener_port || 0);
      if (sortBy === 'players') {
        const playersA = a.currentPlayers ?? 0;
        const playersB = b.currentPlayers ?? 0;
        return playersB - playersA; // Sort descending (most players first)
      }
      return 0;
    });

  return (
    <div className="server-list">
      <div className="list-controls">
        <input
          type="text"
          placeholder="Search servers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
        />
        <div className="sort-dropdown" ref={sortDropdownRef}>
          <button
            className="sort-button"
            onClick={() => setSortOpen(!sortOpen)}
            title="Sort servers"
          >
            <ChevronDown size={16} />
          </button>
          {sortOpen && (
            <div className="sort-menu">
              <button
                className={`sort-option ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('name');
                  setSortOpen(false);
                }}
              >
                Sort by Name
              </button>
              <button
                className={`sort-option ${sortBy === 'status' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('status');
                  setSortOpen(false);
                }}
              >
                Sort by Status
              </button>
              <button
                className={`sort-option ${sortBy === 'port' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('port');
                  setSortOpen(false);
                }}
              >
                Sort by Port
              </button>
              <button
                className={`sort-option ${sortBy === 'players' ? 'active' : ''}`}
                onClick={() => {
                  setSortBy('players');
                  setSortOpen(false);
                }}
              >
                Sort by Players
              </button>
            </div>
          )}
        </div>
      </div>
      {filteredServers.length === 0 ? (
        <div className="no-servers">{searchText ? 'No servers match search' : 'No servers configured yet'}</div>
      ) : (
        filteredServers.map((server) => {
          const serverItem: ServerListItem = {
            id: server.id,
            name: server.name,
            status: server.status,
            currentPlayers: server.currentPlayers,
            scheduledStart: server.scheduledStart,
          };

          return (
            <div
              key={server.id}
              className={`server-item ${selectedId === server.id ? 'active' : ''}`}
              onClick={() => onSelect(serverItem)}
            >
              <div className="server-info">
                <h3>{server.name}</h3>
                <div className="server-port">Port: {server.serverConfig.server_tcp_listener_port}</div>
                <div className="badges-container">
                  <div className={`status-badge ${server.status}`}>
                    <span className="status-dot"></span>
                    {server.status === 'running' ? 'Running' : 'Stopped'}
                  </div>
                  {serverItem.currentPlayers !== undefined && (
                    <div className="players-badge">
                      <Users size={14} />
                      <span>{serverItem.currentPlayers} player{serverItem.currentPlayers !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {server.scheduledStart?.enabled && (
                    <div className="scheduled-badge">
                      <Clock size={14} />
                      <span>
                        {new Date(server.scheduledStart.dateTime).toLocaleString()} {server.scheduledStart.timezone ? `(${getShortTimezoneCode(server.scheduledStart.timezone)})` : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div
                className="server-actions"
                onClick={(e) => e.stopPropagation()}
              >
                {server.status === 'running' ? (
                  <button
                    className="action-button stop"
                    onClick={() => onStop(server.id)}
                    title="Stop server"
                  >
                    <Square size={16} />
                  </button>
                ) : (
                  <button
                    className="action-button launch"
                    onClick={() => onLaunch(server.id)}
                    title="Launch server"
                  >
                    <Play size={16} />
                  </button>
                )}
                {(() => {
                  if (server.status !== 'running' || !broadcastPlatform || !broadcastChannel) return null;
                  const linkedChampionship = championships.find((c) => c.id === server.championshipId);
                  if (!linkedChampionship?.broadcasted) return null;
                  const url = buildBroadcastUrl(broadcastPlatform, broadcastChannel);
                  if (!url) return null;
                  return (
                    <a
                      className="action-button broadcast"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Watch live on ${broadcastPlatform === 'youtube' ? 'YouTube' : 'Twitch'}`}
                    >
                      {broadcastPlatform === 'youtube' ? <Youtube size={16} /> : <Tv size={16} />}
                    </a>
                  );
                })()}
                <button
                  className="action-button delete"
                  onClick={() => onDelete(server.id)}
                  title="Delete server"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ServerList;
